import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type { AdminCompanyDetail, AdminCompanyStatus } from '../../../models/adminCompany';
import {
  fetchAdminCompany,
  logAdminCompanyAudit,
  updateAdminCompanyStatus,
} from '../../../services/admin/adminCompanyService';
import { CompanyStatusPanel } from '../components/CompanyStatusPanel';
import { MerchantListSection } from '../components/MerchantListSection';
import { PendingInvitationsSection } from '../components/PendingInvitationsSection';
import { CompanyUsersSection } from '../components/CompanyUsersSection';
import { CompanyDiscountSection } from '../components/CompanyDiscountSection';
import { StripeCustomerLink } from '../components/StripeCustomerLink';

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
}

export function AdminCompanyDetailPage(): React.ReactElement {
  const { companyId } = useParams<{ companyId: string }>();
  useDocumentTitle('Company detail · Triplicity Backoffice');
  const { getAccessToken } = useAuth();

  const [detail, setDetail] = useState<AdminCompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!companyId) {
      setError('Missing company identifier.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAdminCompany(companyId, getAccessToken);
      setDetail(result);
    } catch (err) {
      setDetail(null);
      setError(err instanceof Error ? err.message : 'Failed to load company detail.');
    } finally {
      setLoading(false);
    }
  }, [companyId, getAccessToken]);

  useEffect(() => {
    logAdminCompanyAudit('access', { route: '/admin/companies/:companyId', companyId });
    void load();
  }, [load, companyId]);

  const handleStatusConfirm = useCallback(
    async (next: AdminCompanyStatus): Promise<void> => {
      if (!companyId) {
        return;
      }
      setStatusMessage(null);
      const updated = await updateAdminCompanyStatus(companyId, next, getAccessToken);
      if (updated) {
        setDetail(updated);
      }
      logAdminCompanyAudit('statusChange', { companyId, status: next });
      setStatusMessage(
        next === 'suspended' ? 'Company suspended successfully.' : 'Company reactivated successfully.'
      );
    },
    [companyId, getAccessToken]
  );

  return (
    <div>
      <PageHeader
        title="Company detail"
        subtitle="Manage a 3PL company's platform access, billing link, and merchants."
      />
      <p>
        <Link to="/admin/dashboard">← Back to dashboard</Link>
      </p>

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading company…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && !detail ? (
        <EmptyState
          title="Company not found"
          description="This company could not be loaded. It may have been removed or the identifier is invalid."
          action={<Link to="/admin/dashboard">Back to dashboard</Link>}
        />
      ) : null}

      {!loading && !error && detail ? (
        <>
          <div className="bo-card">
            <div className="bo-admin-table-head">
              <h2 className="bo-admin-section-title">{detail.name || 'Unnamed company'}</h2>
              <StripeCustomerLink stripeCustomerId={detail.stripeCustomerId} />
            </div>
            <div className="bo-detail-fields-grid">
              <div className="bo-detail-field">
                <span className="bo-detail-field-label">Company ID</span>
                <span className="bo-detail-field-value">{detail.companyId}</span>
              </div>
              <div className="bo-detail-field">
                <span className="bo-detail-field-label">Status</span>
                <span className="bo-detail-field-value">
                  <span
                    className={`bo-status-pill ${
                      detail.status.toLowerCase() === 'active' ? 'bo-status-active' : 'bo-status-inactive'
                    }`}
                  >
                    {detail.status}
                  </span>
                </span>
              </div>
              <div className="bo-detail-field">
                <span className="bo-detail-field-label">Created</span>
                <span className="bo-detail-field-value">{formatDateTime(detail.createdAt)}</span>
              </div>
              {detail.suspendedAt ? (
                <div className="bo-detail-field">
                  <span className="bo-detail-field-label">Suspended</span>
                  <span className="bo-detail-field-value">
                    {formatDateTime(detail.suspendedAt)}
                    {detail.suspendedBy ? ` by ${detail.suspendedBy}` : ''}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          {statusMessage ? (
            <p className="bo-form-success" role="status">
              {statusMessage}
            </p>
          ) : null}

          <CompanyStatusPanel companyName={detail.name} status={detail.status} onConfirm={handleStatusConfirm} />

          <MerchantListSection merchants={detail.merchants} />

          <CompanyUsersSection companyId={detail.companyId} />

          <PendingInvitationsSection
            invitations={detail.pendingInvitations}
            companyId={detail.companyId}
            onResent={() => void load()}
          />

          <CompanyDiscountSection companyId={detail.companyId} getAccessToken={getAccessToken} />
        </>
      ) : null}
    </div>
  );
}
